import React, { useMemo, useRef } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { getSelectedProductRegistry } from '@americanexpress/product-ducks';
import Helmet from 'react-helmet';
import PerformanceMeasure from '@americanexpress/browser-performance-mark';
import type { OneAppModule } from 'one-app-types';
import NavTooltip from './NavTooltip';
import styles from './styles.scss';

export type ConsumerNavigationProps = {
  view: string;
  containerClassName?: string;
  locale: string;
  isContextSwitcherLoading: boolean;
  isSANContainerLoading: boolean;
  hasSanError: boolean;
  lang?: Record<string, string>;
  links?: SiteAreaNavLinks;
  ConsumerContextSwitcher?: OneAppModule<ConsumerContextSwitcherProps>;
  SiteAreaNavContainer?: OneAppModule<SiteAreaNavContainerProps>;
  isTooltipDismissed: boolean;
  isTooltipLoading: boolean;
  dismissTooltip: () => void;
  children?: React.ReactNode;
};

export type ConsumerNavigationType = React.FC<ConsumerNavigationProps>;

const cssBackgroundURL = 'https://www.aexp-static.com/cdaas/one/statics/@americanexpress/static-assets/2.28.0/package/dist/img/brand/worldservice-tile-gray.svg';

// make an external const so it doesnt trigger updates
const iconsMap = {};

const ConsumerNavigation: ConsumerNavigationType = ({
  view,
  containerClassName = '',
  locale,
  isContextSwitcherLoading,
  isSANContainerLoading,
  hasSanError,
  lang,
  links,
  ConsumerContextSwitcher,
  SiteAreaNavContainer,
  isTooltipDismissed,
  isTooltipLoading,
  dismissTooltip,
  children,
}) => {
  const consumerNavRef = useRef<HTMLDivElement>(null);

  const { opaqueAccountId } = useSelector(getSelectedProductRegistry, shallowEqual) || {};

  const isLoadedWithoutError = !(isContextSwitcherLoading || isSANContainerLoading || hasSanError);
  const shouldRenderNav = view !== 'PRODUCT' || opaqueAccountId;

  const shouldRenderContextSwitcher = !isContextSwitcherLoading && !!ConsumerContextSwitcher;
  const shouldRenderSANContainer = !isSANContainerLoading && !!SiteAreaNavContainer && !hasSanError;

  // define props that when update force the SAN to rerender
  const navRefreshProps = useMemo(() => {
    const accountProps = view === 'PRODUCT' ? [opaqueAccountId] : [];
    return [view, locale, ...accountProps];
  }, [view, locale, opaqueAccountId]);

  return (
    <>
      <Helmet
        link={[{
          rel: 'preload',
          as: 'image',
          href: cssBackgroundURL,
        }]}
      />
      {shouldRenderNav && (
        <>
          <div className={`nav width-full position-fixed border-b ${styles.consumerNav}`} ref={consumerNavRef}>
            <div className="width-full container pad-0-sm-down">
              <div className="flex">
                <div className="col-xs-6 col-md-4 border-l-md-up">
                  <div className="row flex-column height-full flex-justify-center" data-testid="context_switcher_container">
                    {shouldRenderContextSwitcher && (
                      <ConsumerContextSwitcher
                        view={view}
                        listboxOffsetRef={consumerNavRef}
                      />
                    )}
                  </div>
                </div>
                <div className="col-xs-6 col-md-8 border-l">
                  <div className={`row ${styles.siteAreaNavWrapper}`} data-testid="site_area_nav_container">
                    {shouldRenderSANContainer && (
                      <NavTooltip
                        view={view}
                        displayText={lang?.navTooltipDisplayText}
                        dismissText={lang?.navTooltipDismissText}
                        isTooltipDismissed={isTooltipDismissed}
                        isTooltipLoading={isTooltipLoading}
                        dismissTooltip={dismissTooltip}
                      >
                        <SiteAreaNavContainer
                          links={links}
                          icons={iconsMap}
                          defaultSelectedText={lang?.defaultSelectedText}
                          moreLinkText={lang?.moreLinkText}
                          navRefreshProps={navRefreshProps}
                        />
                      </NavTooltip>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          { /* offsets content below by height of fixed nav above */ }
          <div className={styles.navShim} />
        </>
      )}
      <div data-testid="page_content_wrapper" className={`${view === 'ACCOUNT' ? styles.gutterBackground : ''}`}>
        <div
          data-testid="page_content_container"
          className={containerClassName}
        >
          {children}
        </div>
      </div>
      <PerformanceMeasure moduleName="axp-consumer-navigation" variable={isLoadedWithoutError} />
    </>
  );
};

export default ConsumerNavigation;
